import type { AccountResponse } from "@ccflare/api";
import { isOAuthProvider, type OAuthProvider } from "@ccflare/types";
import { AlertCircle, Download, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/auth-context";
import { useAccountsPageModel } from "../hooks/useAccountsPageModel";
import {
	AccountAddForm,
	AccountList,
	DeleteConfirmationDialog,
	RenameAccountDialog,
} from "./accounts";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

export function AccountsTab() {
	const model = useAccountsPageModel();
	const { gatewayMode, oauthCatcherAvailable } = useAuth();

	const [adding, setAdding] = useState(false);
	// Re-login target: reuses the add-account OAuth form in re-auth mode.
	const [reauthTarget, setReauthTarget] = useState<{
		id: string;
		name: string;
		provider: OAuthProvider;
	} | null>(null);

	const handleReauth = (account: AccountResponse) => {
		if (!isOAuthProvider(account.provider)) return;
		setAdding(false);
		model.clearError();
		setReauthTarget({
			id: account.id,
			name: account.name,
			provider: account.provider,
		});
	};
	const [confirmDelete, setConfirmDelete] = useState<{
		show: boolean;
		accountId: string;
		accountName: string;
		confirmInput: string;
	}>({
		show: false,
		accountId: "",
		accountName: "",
		confirmInput: "",
	});
	const [renameDialog, setRenameDialog] = useState<{
		isOpen: boolean;
		account: AccountResponse | null;
	}>({
		isOpen: false,
		account: null,
	});

	const handleRemoveAccount = (account: AccountResponse) => {
		setConfirmDelete({
			show: true,
			accountId: account.id,
			accountName: account.name,
			confirmInput: "",
		});
	};

	const handleConfirmDelete = async () => {
		if (confirmDelete.confirmInput !== confirmDelete.accountName) {
			return;
		}
		await model.removeAccount(confirmDelete.accountId);
		setConfirmDelete({
			show: false,
			accountId: "",
			accountName: "",
			confirmInput: "",
		});
	};

	const handleConfirmRename = async (newName: string) => {
		if (!renameDialog.account) return;
		await model.renameAccount(renameDialog.account.id, newName);
		setRenameDialog({ isOpen: false, account: null });
	};

	if (model.loading) {
		return (
			<Card>
				<CardContent className="pt-6">
					<p className="text-muted-foreground">Loading accounts...</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			{model.error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="flex items-center gap-2">
							<AlertCircle className="h-4 w-4 text-destructive" />
							<p className="text-destructive">{model.error}</p>
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Accounts</CardTitle>
							<CardDescription>
								Manage provider accounts and authentication settings
							</CardDescription>
						</div>
						{!adding && !reauthTarget && (
							<Button onClick={() => setAdding(true)} size="sm">
								<Plus className="mr-2 h-4 w-4" />
								Add Account
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent>
					{gatewayMode && oauthCatcherAvailable && (
						<div className="mb-4 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
							<Download className="mt-0.5 h-4 w-4 shrink-0" />
							<span>
								Adding an account via OAuth on a hosted gateway? Run the{" "}
								<a
									href="/api/oauth-catcher"
									className="font-medium text-primary underline underline-offset-2"
								>
									OAuth code catcher
								</a>{" "}
								(.zip) on your machine to capture the authorization code from
								the provider redirect, then paste it into the complete step.
							</span>
						</div>
					)}
					{adding && (
						<AccountAddForm
							onCreateApiKeyAccount={async (params) => {
								await model.createApiKeyAccount(params);
								setAdding(false);
							}}
							onStartOAuth={model.startOAuth}
							onCompleteOAuth={model.completeOAuth}
							onGetSessionStatus={model.getSessionStatus}
							onOAuthCompleted={model.onOAuthCompleted}
							onCancel={() => {
								setAdding(false);
								model.clearError();
							}}
							onSuccess={() => {
								setAdding(false);
							}}
							onError={() => {}}
							groups={gatewayMode ? model.groups : undefined}
						/>
					)}

					{/* Modal, not inline: the Re-login button is usually far down the
					    list, so an inline form at the top of the card opens off-screen. */}
					<Dialog
						open={!!reauthTarget}
						onOpenChange={(open) => {
							if (!open) {
								setReauthTarget(null);
								model.clearError();
							}
						}}
					>
						<DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
							<DialogTitle className="sr-only">Re-login</DialogTitle>
							{reauthTarget && (
								<AccountAddForm
									key={reauthTarget.id}
									reauthAccount={reauthTarget}
									onCreateApiKeyAccount={async () => {}}
									onStartOAuth={model.startOAuth}
									onCompleteOAuth={model.completeOAuth}
									onGetSessionStatus={model.getSessionStatus}
									onOAuthCompleted={model.onOAuthCompleted}
									onCancel={() => {
										setReauthTarget(null);
										model.clearError();
									}}
									onSuccess={() => {
										setReauthTarget(null);
									}}
									onError={() => {}}
								/>
							)}
						</DialogContent>
					</Dialog>

					<AccountList
						accounts={model.accounts}
						onReauth={handleReauth}
						onPauseToggle={(account) => model.togglePause(account)}
						onRemove={handleRemoveAccount}
						onRename={(account) => setRenameDialog({ isOpen: true, account })}
						groups={gatewayMode ? model.groups : undefined}
						onMoveGroup={
							gatewayMode
								? (account, groupId) =>
										void model.moveAccountGroup(account.id, groupId)
								: undefined
						}
					/>
				</CardContent>
			</Card>

			{confirmDelete.show && (
				<DeleteConfirmationDialog
					accountName={confirmDelete.accountName}
					confirmInput={confirmDelete.confirmInput}
					onConfirmInputChange={(value) =>
						setConfirmDelete({
							...confirmDelete,
							confirmInput: value,
						})
					}
					onConfirm={handleConfirmDelete}
					onCancel={() => {
						setConfirmDelete({
							show: false,
							accountId: "",
							accountName: "",
							confirmInput: "",
						});
						model.clearError();
					}}
				/>
			)}

			{renameDialog.isOpen && renameDialog.account && (
				<RenameAccountDialog
					isOpen={renameDialog.isOpen}
					currentName={renameDialog.account.name}
					onClose={() => setRenameDialog({ isOpen: false, account: null })}
					onRename={handleConfirmRename}
					isLoading={model.isRenaming}
				/>
			)}
		</div>
	);
}
